"use client";

import React, { useEffect, useRef, useState } from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { Activity, Map, Users, Fish } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  value,
  suffix = ""
}: {
  value: number;
  suffix?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2500;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * value));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(value);
      }
    };
    window.requestAnimationFrame(step);
  }, [isVisible, value]);
  return <span ref={ref} className="tabular-nums">
      {count.toLocaleString("hu-HU")}
      {suffix}
    </span>;
}
const statsData = [{
  id: "species",
  label: "Elérhető Szálláshelyek",
  value: 500000,
  suffix: "+",
  icon: Fish,
  chartData: [{
    month: "Jan",
    value: 400000
  }, {
    month: "Feb",
    value: 420000
  }, {
    month: "Már",
    value: 450000
  }, {
    month: "Ápr",
    value: 470000
  }, {
    month: "Máj",
    value: 490000
  }, {
    month: "Jún",
    value: 500000
  }]
}, {
  id: "anglers",
  label: "Elégedett Utazók",
  value: 125000,
  suffix: "",
  icon: Users,
  chartData: [{
    month: "Jan",
    value: 80000
  }, {
    month: "Feb",
    value: 95000
  }, {
    month: "Már",
    value: 102000
  }, {
    month: "Ápr",
    value: 110000
  }, {
    month: "Máj",
    value: 118000
  }, {
    month: "Jún",
    value: 125000
  }]
}, {
  id: "guides",
  label: "Légitársasági Partnerek",
  value: 240,
  suffix: "",
  icon: Activity,
  chartData: [{
    month: "Jan",
    value: 180
  }, {
    month: "Feb",
    value: 195
  }, {
    month: "Már",
    value: 210
  }, {
    month: "Ápr",
    value: 220
  }, {
    month: "Máj",
    value: 235
  }, {
    month: "Jún",
    value: 240
  }]
}, {
  id: "maps",
  label: "Úticélok Világszerte",
  value: 180,
  suffix: "+",
  icon: Map,
  chartData: [{
    month: "Jan",
    value: 120
  }, {
    month: "Feb",
    value: 135
  }, {
    month: "Már",
    value: 150
  }, {
    month: "Ápr",
    value: 160
  }, {
    month: "Máj",
    value: 175
  }, {
    month: "Jún",
    value: 180
  }]
}];
const chartConfig = {
  value: {
    label: "Növekedés",
    color: "var(--primary)"
  }
};
export default function CommunityStatistics() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true
  });
  return <section data-section-id="224659" id="globális-elérésünk" className="bg-secondary text-secondary-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[50%] w-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] h-[50%] w-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.03] grayscale invert" style={{
        backgroundImage: "url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645649/img/world_map_pattern.png')",
        backgroundSize: 'cover'
      }} />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("mx-auto mb-16 max-w-2xl text-center transition-all duration-1000", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-secondary-foreground">
            Globális Elérésünk Számokban
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-default">
            A TRAVEL technológiája és kiterjedt partnerhálózata biztosítja, hogy Ön bárhol a világon megtalálja a tökéletes szállást és utazási lehetőséget.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {statsData.map((stat, i) => {
          const Icon = stat.icon;
          return <div data-index={i} key={stat.id}>
                <Card className="group relative overflow-hidden border-border/10 bg-card/5 backdrop-blur-md transition-all duration-500 hover:border-primary/30 hover:bg-card/10 hover:shadow-4xl hover:shadow-black/20 rounded-2xl">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="font-default text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      {stat.label}
                    </CardTitle>
                    <div className="rounded-full bg-primary/20 p-2 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="h-4 w-4" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="font-default text-4xl font-bold tracking-tighter text-secondary-foreground sm:text-5xl">
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                    </div>
                    
                    <div className="mt-6 h-[60px] w-full opacity-40 transition-opacity duration-300 group-hover:opacity-100">
                      <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={stat.chartData} margin={{
                        top: 5,
                        right: 5,
                        left: 5,
                        bottom: 5
                      }}>
                            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel hideIndicator />} />
                            <Line type="monotone" dataKey="value" stroke="hsl(210, 100%, 50%)" strokeWidth={3} dot={false} activeDot={{
                          r: 5,
                          fill: "var(--primary)",
                          stroke: "var(--secondary)",
                          strokeWidth: 2
                        }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}